import { useMemo, useState } from 'react';
import { t } from 'i18next';
import { useEventAnalytics as useEventAnalyticsWithRepository } from '../../../application';
import { EventRecord } from '../../../domain/entities';
import { ClaimRequestStatus } from '../../../domain/values';
import { EventAnalyticsNomaRepository } from '../../../infraestructure';
import { Button, ButtonVariant, Card, HistorialIcon, ProgressIndicator } from '../../atoms';
import ClaimStatusChip from '../claim-status-chip/ClaimStatusChip';
import Timeline from '../timeline/Timeline';
import ReferralApprovalModal from './ReferralApprovalModal';
import './claim-lifecycle.modules.scss';

export type ClaimLifecycleProps = {
    status: ClaimRequestStatus;
    claimRequestKey: string;
    statusDetails?: string | string[];
    onBindReferralApproval?: (file?: File) => Promise<void>;
};

const ClaimLifecycle = ({ status, claimRequestKey, statusDetails, onBindReferralApproval }: ClaimLifecycleProps) => {
    const useEventAnalytics = useMemo(() => useEventAnalyticsWithRepository(EventAnalyticsNomaRepository), []);
    const { findLifecycle } = useEventAnalytics();
    const [lifecycle, setLifecycle] = useState<EventRecord[] | undefined>(undefined);
    const [lifecycleLoader, setLifeCycleLoader] = useState(false);
    const [isReferralModalOpen, setIsReferralModalOpen] = useState(false);

    const toggleLifecycleView = () => {
        if (lifecycle && lifecycle.length > 0) {
            setLifecycle(undefined);
        } else {
            setLifeCycleLoader(true);
            findLifecycle(claimRequestKey).then((values) => {
                if (values) {
                    setLifecycle(values);
                }
                setLifeCycleLoader(false);
            });
        }
    };

    const handleOpenReferralModal = () => {
        setIsReferralModalOpen(true);
    };

    const handleCloseReferralModal = () => {
        setIsReferralModalOpen(false);
    };

    const handleSubmitReferralApproval = async (file?: File) => {
        if (onBindReferralApproval) {
            await onBindReferralApproval(file);
        }
        handleCloseReferralModal();
    };

    return (
        <Card className="lifecycle-card" title={t('claim_lifecycle.status')}>
            <ClaimStatusChip
                status={status}
                details={statusDetails}
                colored
                onClick={() => navigator.clipboard.writeText(claimRequestKey)}
            />
            {status == ClaimRequestStatus.PENDING_MUNICH_RE && (
                <Button
                    accessibilityText={t('claim_lifecycle.bind_referral_approval')}
                    text={t('claim_lifecycle.bind_referral_approval')}
                    onClick={handleOpenReferralModal}
                    variantName={ButtonVariant.BASIC_INFO}
                />
            )}
            <Button
                accessibilityText={t('claim_lifecycle.lifecycle')}
                icon={HistorialIcon}
                onClick={() => toggleLifecycleView()}
                variantName={ButtonVariant.BASIC_INFO}
            />
            <div className="lifecycle-container">
                {!lifecycleLoader ? (
                    lifecycle && (
                        <Timeline
                            items={lifecycle.map((event) => ({
                                name: event.name,
                                timestamp: event.timestamp,
                                subtitle: event.agentFirstName
                                    ? event.agentFirstName + ' ' + (event.agentLastName ?? '')
                                    : undefined,
                                payload: event.payload,
                            }))}
                        ></Timeline>
                    )
                ) : (
                    <ProgressIndicator />
                )}
            </div>
            <ReferralApprovalModal
                isOpen={isReferralModalOpen}
                onClose={handleCloseReferralModal}
                onSubmit={handleSubmitReferralApproval}
            />
        </Card>
    );
};
export default ClaimLifecycle;
